import { useTranslation } from 'next-i18next';
import { Fragment, useEffect, useState } from 'react';
import { useBooth } from '../../../../../../hooks/useBooth';
import useMessaging from '../../../../../../hooks/useMessaging';
import { useMobileApp } from '../../../../../../hooks/useMobileApp';
import { Booth, LinkedPerson, Meetingroom } from '../../../../../../interfaces/interfaces';
import { useSelector } from '../../../../../../reduxToolkit/typedRedux';
import { richTextContentFormat } from '../../../../../../services/helper';
import SlickSlider from '../../../../SlickSliderNew';
import AppointmentBoard from '../../appointment/AppointmentBoard';
import MeetingRoomTemplate from '../../MeetingRoomTemplate';
import NetworkRoom from '../../network/NetworkRoom';
import PDFMaterial from '../../PDFMaterial';
import BigBoothHeader from '../BigBoothHeader';
import Section from '../BigBoothTwoColsSection';
import BoothLinkedPerson from '../BoothLinkedPerson';
import BoothVideos from '../BoothVideos';
import Breadcrumb from '../Breadcrumb';
import ChildBooth from '../ChildBooth';

interface BigBoothPreviewInterface {
  booth?: Booth;
}

interface Section {
  title: string;
  meetingrooms: Meetingroom[] | undefined;
  enabled: boolean | undefined;
}

const BigBoothPreview = ({ booth, ...props }: BigBoothPreviewInterface) => {
  const { t } = useTranslation(['booth']);
  const { openMessagingConversationFromAttendee } = useMessaging();
  const { checkAppointmentsExist } = useBooth();
  const { isAppView } = useMobileApp();

  const { event, networks, isMobilePreview } = useSelector(({ common }) => ({
    event: common.event,
    networks: common.networks,
    isMobilePreview: common.isMobilePreview,
  }));

  const [meetingTypeMeetingrooms, setMeetingTypeMeetingrooms] = useState<Meetingroom[]>();
  const [myPresentations, setMyPresentations] = useState<Meetingroom[]>();
  const [meetingTypeConferencerooms, setMeetingTypeConferencerooms] = useState<Meetingroom[]>();

  useEffect(() => {
    if (!booth) return;
    setMeetingTypeMeetingrooms(booth.meetingrooms);
    setMyPresentations(booth.presentationrooms);
    setMeetingTypeConferencerooms(booth.conferencerooms);
  }, [booth]);

  const sections: Section[] = [
    {
      title: event?.template.individualNames.meetingrooms || t('booth:chatrooms'),
      meetingrooms: meetingTypeMeetingrooms,
      enabled: true,
    },
    {
      title: event?.template.individualNames.conferencerooms || t('booth:chatrooms'),
      meetingrooms: meetingTypeConferencerooms,
      enabled: true,
    },
    {
      title: event?.template.individualNames.presentationrooms || t('booth:chatrooms'),
      meetingrooms: myPresentations,
      enabled: !!booth?.settings?.displayPresentationsInBooths?.value,
    },
  ];

  const onLinkedPersonClick = (user: LinkedPerson['user']) => {
    openMessagingConversationFromAttendee(user);
  };

  if (!booth) return <></>;

  const boothNetworks = networks.filter((network) => network?.booth?._id === booth._id);
  const appointmentsExist = checkAppointmentsExist(booth?.meetingrooms);

  return (
    <>
      <div {...props} className="relative bg-white">
        {booth && <SlickSlider slides={booth?.headerImages || []} autoplay={booth?.settings?.autoSlideshow?.value ?? false} />}
        <div className="sticky top-0 z-30 w-full py-2 bg-white shadow-sm">
          <BigBoothHeader booth={booth} />
        </div>
        <Breadcrumb booth={booth} className="px-12 pt-4 pb-3" />
        <main className="px-8 pb-8">
          <div
            style={{ marginTop: '0.5rem' }}
            className={`flex flex-wrap ${
              booth?.boothVideos && booth?.boothVideos?.length > 0 && !isMobilePreview ? 'lg:grid lg:grid-cols-2 lg:gap-4 lg:px-4' : ''
            }`}>
            {booth && <BoothVideos booth={booth} />}
            <div className={`flex flex-col h-full justify-between w-full ${isMobilePreview ? '' : 'lg:w-auto'}`}>
              <div
                className={`reset grow overflow-y-auto ${isAppView() ? '' : 'h-48'}`}
                dangerouslySetInnerHTML={{
                  __html: richTextContentFormat(booth?.description),
                }}
              />
              <div className="grid grid-flow-col gap-4 mt-2 grow-0">
                {booth?.websiteUrl && (
                  <button
                    className="py-1 text-xs rounded bg-template bg-buttons-main templateBorder"
                    onClick={() => {
                      let url = booth.websiteUrl;
                      if (booth.websiteUrl && !/^http[s]?:\/\//i.test(booth.websiteUrl)) {
                        url = `http://${booth.websiteUrl}`;
                      }
                      window.open(url, '_blank');
                    }}>
                    {t('booth:to-our-website')}
                  </button>
                )}
              </div>
            </div>
          </div>
          {booth.childBooths && booth.childBooths.length > 0 && (
            <Section title={event?.template.individualNames.subBooths || ''}>
              {booth.childBooths.map((booth) => (
                <ChildBooth key={booth._id} booth={booth} />
              ))}
            </Section>
          )}
          {event?.settings?.isLinkedBoothMembersActive && booth.linkedPeople && booth.linkedPeople.length > 0 && (
            <Section title={event?.template.individualNames.linkedPeople ?? t('booth:linked-people')}>
              {booth.linkedPeople
                .map((person) => ({ ...person, avatar: person.avatar ?? person.user.avatar, name: person.name ?? person.user.name }))
                .map((person) => (
                  <BoothLinkedPerson key={person._id} person={person} onLinkedPersonClick={onLinkedPersonClick} />
                ))}
            </Section>
          )}
          {sections.map((section, index) => {
            return (
              <Fragment key={section.title + index}>
                {section && section?.meetingrooms && section?.meetingrooms?.length > 0 && section.enabled && (
                  <Section title={section.title}>
                    {section?.meetingrooms?.map((meetingroom) => {
                      if (booth) return <MeetingRoomTemplate key={meetingroom._id} meetingroom={meetingroom} booth={booth} />;
                    })}
                  </Section>
                )}
              </Fragment>
            );
          })}
          {event?.modules?.booking?.active && booth?.settings?.enableAppointments.value && booth?.meetingrooms?.length > 0 && appointmentsExist && (
            <Section title={event.template.individualNames.appointments} twoCols={false}>
              <div className="w-full pt-2 pb-4 overflow-auto">
                <AppointmentBoard
                  event={event}
                  date={event.datetime_start}
                  endDate={event.datetime_end}
                  meetingrooms={booth?.meetingrooms}
                  bookingmode
                  boothId={booth._id}
                />
              </div>
            </Section>
          )}
          {booth?.settings?.displayNetworkingroomsInBooth?.value && boothNetworks.length > 0 && (
            <Section title={t('booth:networking-rooms')} twoCols={false}>
              {boothNetworks.map((network) => (
                <div className="flex justify-center w-full pt-2 pb-4 overflow-auto">
                  <NetworkRoom key={network._id} network={network} />
                </div>
              ))}
            </Section>
          )}
          {booth?.pdfUploads && booth?.pdfUploads?.length > 0 && (
            <Section title={event?.template.individualNames.pdfUploadsInBooth || ''}>
              {booth.pdfUploads?.map((pdf) => (
                <PDFMaterial key={pdf._id} pdf={pdf} />
              ))}
            </Section>
          )}
        </main>
      </div>
    </>
  );
};

export default BigBoothPreview;
